import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";

import bg1 from '../../../assets/images/bg/blog02.jpg';
import NavbarTwo from "../../../components/navbar/navbarTwo";
import FooterThree from "../../../components/footer/footerThree";
import ScrollTop from "../../../components/scrollTop";
import AdminCreateRecipesBlogPost from "../../../components/blog/adminCreateRecipesBlogPost";

import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

// Firebase imports
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from "firebase/auth";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import app from "../../../firebase/firebase";

// Import authors data from your authors.js file
import { authors } from "../../../data/authors";

// Initialize Firebase Auth and Firestore
const auth = getAuth(app);
const db = getFirestore(app);

export default function RecipesBlog() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  // Helper function to format the timestamp to "29th Nov 2023"
  const formatTimestamp = (timestamp) => {
    let formattedDate = "Unknown date";
    if (timestamp && timestamp.toDate) {
      const dateObj = timestamp.toDate();
      const day = dateObj.getDate();
      const suffix =
        day % 100 >= 11 && day % 100 <= 13
          ? "th"
          : day % 10 === 1
          ? "st"
          : day % 10 === 2
          ? "nd"
          : day % 10 === 3
          ? "rd"
          : "th";
      const month = dateObj.toLocaleString("en-US", { month: "short" });
      const year = dateObj.getFullYear();
      formattedDate = `${day}${suffix} ${month} ${year}`;
    }
    return formattedDate;
  };

  // Listen for auth state changes.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        user
          .getIdTokenResult()
          .then((idTokenResult) => {
            if (idTokenResult.claims.admin) {
              setIsAuthenticated(true);
            } else {
              setIsAuthenticated(false);
            }
            setCurrentUserEmail(user.email);
          })
          .catch((error) => {
            console.error("Error retrieving token claims:", error);
            setIsAuthenticated(false);
            setCurrentUserEmail(user.email);
          });
      } else {
        setIsAuthenticated(false);
        setCurrentUserEmail(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch posts from Firestore's "recipes-blog" collection.
  useEffect(() => {
    async function fetchPosts() {
      setLoadingPosts(true);
      try {
        const postsCollection = collection(db, "recipes-blog");
        const querySnapshot = await getDocs(postsCollection);
        const postsData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            image: data["header-image-url"],
            desc: data.description,
            tag: data.tags && data.tags.length > 0 ? data.tags[0] : "",
            author: data.author,
            timestamp: data.timestamp, // This remains a Firestore timestamp object
          };
        });
        setPosts(postsData);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoadingPosts(false);
      }
    }
    fetchPosts();
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      user.getIdTokenResult().then((idTokenResult) => {
        if (idTokenResult.claims.admin) {
          setIsAuthenticated(true);
          setCurrentUserEmail(user.email);
        } else {
          alert("You do not have admin privileges.");
          signOut(auth);
        }
      });
    } catch (error) {
      console.error("Error signing in", error);
      alert("Error signing in");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setIsAuthenticated(false);
      setCurrentUserEmail(null);
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  return (
    <>
      <NavbarTwo navClass="defaultscroll sticky" manuClass="navigation-menu nav-light nav-right" />
      <section
        className="bg-half-170 d-table w-100"
        style={{ backgroundImage: `url(${bg1})` }}
      >
        <div className="bg-overlay bg-gradient-overlay"></div>
        <div className="container">
          <div className="row mt-5 justify-content-center">
            <div className="col-12">
              <div className="title-heading text-center">
                <h5 className="heading fw-semibold page-heading text-white title-dark">
                  Recipes Blog
                </h5>
                <p className="text-white-50 para-desc mx-auto mb-0">
                  Our Latest Recipes in Image Masonry Layouts
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <AdminCreateRecipesBlogPost isAuthenticated={isAuthenticated} />
          <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 768: 2, 991: 3 }}>
            <Masonry>
              {loadingPosts ? (
                // Render MUI Skeletons while loading
                Array.from(new Array(6)).map((_, index) => (
                  <div className="p-2" key={index}>
                    <div className="card blog blog-image blog-primary shadow rounded overflow-hidden">
                      <div className="card-img">
                        <Skeleton variant="rectangular" width="100%" height={200} />
                        <div className="card-overlay"></div>
                        <div className="position-absolute top-0 start-0 mt-3 ms-4">
                          <Skeleton variant="text" width={50} height={24} />
                        </div>
                      </div>
                      <div className="content px-4">
                        <Skeleton variant="text" width="80%" height={30} />
                        <div className="d-flex author align-items-center mt-2">
                          <Skeleton variant="circular" width={40} height={40} />
                          <div className="ms-2">
                            <Skeleton variant="text" width={100} height={16} />
                            <Skeleton variant="text" width={60} height={12} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                // Render fetched posts
                posts.map((item, index) => {
                  // Lookup author info for the current post.
                  const authorInfo = authors.find((author) => author.name === item.author);
                  return (
                    <div className="p-2" key={index}>
                      <div className="card blog blog-image blog-primary shadow rounded overflow-hidden">
                        <div className="card-img">
                          <img src={item.image} className="img-fluid" alt="" />
                          <div className="card-overlay"></div>
                          {item.tag && (
                            <div className="position-absolute top-0 start-0 mt-3 ms-4">
                              <Link to="#" className="badge text-bg-light">
                                {item.tag}
                              </Link>
                            </div>
                          )}
                        </div>
                        <div className="content px-4">
                          <Link
                            to={`/recipes-blog/${item.id}`}
                            className="h5 title d-block mb-0 text-white title-dark"
                          >
                            {item.title}
                          </Link>
                          <div className="d-flex author align-items-center mt-2">
                            {/* Using a placeholder avatar if authorInfo is not found */}
                            <img
                              src={authorInfo ? authorInfo.image : "https://via.placeholder.com/40"}
                              className="avatar avatar-md-sm rounded-pill"
                              alt=""
                            />
                            <div className="ms-2">
                              <small className="text-white-50 user d-block">{item.author}</small>
                              <small className="text-white-50 date">
                                {formatTimestamp(item.timestamp)}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </Masonry>
          </ResponsiveMasonry>

          <div className="row mt-4">
            <div className="col-12">
              <ul className="pagination justify-content-center mb-0">
                <li className="page-item">
                  <Link className="page-link" to="#" aria-label="Previous">
                    <span aria-hidden="true">
                      <i className="mdi mdi-chevron-left mdi-18px"></i>
                    </span>
                  </Link>
                </li>
                <li className="page-item">
                  <Link className="page-link active" to="#">
                    1
                  </Link>
                </li>
                <li className="page-item">
                  <Link className="page-link" to="#" aria-label="Next">
                    <span aria-hidden="true">
                      <i className="mdi mdi-chevron-right mdi-18px"></i>
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Google Sign In / Sign Out Section */}
      <div className="text-center my-4">
        {isAuthenticated ? (
          <div>
            <p>
              Signed in as {currentUserEmail}. You can now create a new blog post!
            </p>
            <button onClick={handleSignOut} className="btn btn-danger">
              Sign Out
            </button>
          </div>
        ) : (
          <button onClick={handleGoogleSignIn} className="btn btn-secondary">
            Admin Sign In
          </button>
        )}
      </div>

      <FooterThree />
      <ScrollTop />
    </>
  );
}
